/**
 * KAN-724: выбор клиента организации по поиску (имя, телефон, email, компания).
 * Поиск идёт через список клиентов (GET /api/v1/clients/?search=), первые 8 совпадений.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useDebounce } from '@/hooks/useDebounce';
import { clientsApi } from '@/services/api/clients';
import type { Client } from '@/types/client';

const PICKER_PAGE_SIZE = 8;
const MIN_SEARCH = 2;

interface Props {
  selectedId: string | null;
  onSelect: (client: Client) => void;
}

function clientLine(client: Client): string {
  return [client.company, client.email, client.phone].filter(Boolean).join(' · ');
}

export function ClientSearchPicker({ selectedId, onSelect }: Props): JSX.Element {
  const [search, setSearch] = useState('');
  const term = useDebounce(search.trim(), 300);
  const enabled = term.length >= MIN_SEARCH;
  const { data, isFetching } = useQuery({
    queryKey: ['clients', 'picker', term],
    queryFn: () => clientsApi.list({ search: term, page_size: PICKER_PAGE_SIZE }),
    enabled,
    staleTime: 15_000,
  });
  const clients = enabled ? data?.results ?? [] : [];

  return (
    <div className="space-y-2" data-testid="client-picker">
      <Input
        aria-label="Поиск клиента"
        data-testid="client-picker-search"
        placeholder="Имя, телефон, email или компания"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        autoFocus
      />
      {enabled && !isFetching && clients.length === 0 && (
        <p className="text-sm text-gray-500" data-testid="client-picker-empty">
          Клиенты не найдены
        </p>
      )}
      {clients.length > 0 && (
        <ul role="listbox" aria-label="Клиенты" className="max-h-60 overflow-y-auto rounded-md border">
          {clients.map((client) => {
            const selected = client.id === selectedId;
            return (
              <li key={client.id} role="option" aria-selected={selected}>
                <button
                  type="button"
                  data-testid={`client-picker-option-${client.id}`}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                    selected ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => onSelect(client)}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{client.name || 'Без имени'}</span>
                    {clientLine(client) && (
                      <span className="block truncate text-xs text-gray-500">{clientLine(client)}</span>
                    )}
                  </span>
                  {selected && <Check className="h-4 w-4 shrink-0 text-blue-600" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
